<template>
    <Portal to="modal">
        <ModalContainer key="inviteModal">
            <template #head>
                <div class="invite__head">
                    <img
                        class="invite__logo"
                        src="@/assets/logos/logo.svg"
                        alt="Pocket Prep Logo"
                    >
                    <div class="invite__head-right">
                        <RouterLink
                            v-slot="{ navigate }"
                            custom
                            :to="signInHref"
                        >
                            <PocketLink
                                class="invite__have-account-link"
                                type="tertiary-small"
                                :href="signInHref"
                                @click="navigate"
                                @mousedown.prevent
                            >
                                I have an account
                            </PocketLink>
                        </RouterLink>
                        <span>•</span>
                        <PocketLink
                            class="invite__help-link"
                            type="tertiary-small"
                            @click="showIntercom"
                            @keydown.enter="showIntercom"
                            @mousedown.prevent
                        >
                            Help
                        </PocketLink>
                    </div>
                </div>
            </template>
            <div class="invite">
                <LeftBlob class="invite__left-blob" />
                <RightBlob class="invite__right-blob" />
                <div class="invite__title">
                    Activate Your License Code
                </div>
                <div
                    v-if="mappedExams.length > 1"
                    class="invite__info"
                >
                    {{ orgName }} has given you <span class="invite__premium-access">premium access</span> to
                    <span v-if="license" class="invite__premium-access">
                        prepare for {{ mappedExams.length }} exams
                    </span>.
                    We need some basic information to create your account.
                </div>
                <div
                    v-else
                    class="invite__info"
                >
                    {{ orgName }} has provided
                    <span class="invite__premium-access">premium access to prepare for </span>
                    <span
                        v-if="license"
                        class="invite__exam-name"
                    >
                        the {{ license.examName }}.
                    </span>
                    <span
                        v-else
                        class="invite__exam-name"
                    >
                        your exam.
                    </span>
                    We need some basic information to create your account.
                </div>
                <PocketInput
                    v-model="firstName"
                    class="invite__first-name"
                    label="First Name"
                    :disabled="isLoading"
                    :error="errorFields.includes('firstName')"
                    @keydown.enter="submitAccountDetails"
                />
                <PocketInput
                    v-model="lastName"
                    class="invite__last-name"
                    label="Last Name"
                    :disabled="isLoading"
                    :error="errorFields.includes('lastName')"
                    @keydown.enter="submitAccountDetails"
                />
                <PocketSelect
                    v-if="licenseCode && !errors.length && mappedExams.length > 1"
                    class="invite__select-exam"
                    v-model="exam"
                    label="Select Exam (You can switch anytime)"
                    :data="mappedExams"
                />
                <div class="invite__email-container">
                    <PocketInput
                        v-model="email"
                        class="invite__email"
                        label="Email"
                        field-type="email"
                        :disabled="emailQueryParam && license ? true : false"
                        :error="errorFields.includes('email')"
                        @keydown.enter="submitAccountDetails"
                    />
                    <div v-if="emailQueryParam && license" class="invite__email-tip">
                        <span class="invite__email-tip-title">
                            Your license code is associated with this email.
                        </span>
                        <div class="invite__email-tip-description">
                            If you already have an account that you would prefer to use
                            <!-- eslint-disable max-len -->
                            <PocketLink
                                class="invite__email-tip-link"
                                target="_blank"
                                href="http://help.educators.pocketprep.com/en/articles/5416702-how-do-i-claim-a-pocket-prep-license"
                            >
                                click here
                            </PocketLink>
                        <!-- eslint-enable max-len -->
                        </div>
                    </div>
                </div>
                <PocketInput
                    v-if="!licenseQueryParam && !groupCodeQueryParam"
                    v-model="licenseCode"
                    class="invite__license"
                    label="License Code"
                    :disabled="isLoading"
                    :error="errorFields.includes('license')"
                    @keydown.enter="submitAccountDetails"
                    @focusout="validateLicenseCode"
                />
                <PocketInput
                    v-if="createPassword"
                    v-model="password"
                    class="invite__password"
                    label="Create Password"
                    labelSubtext="10+ characters, including uppercase and a number"
                    field-type="password"
                    :disabled="isLoading"
                    :error="errorFields.includes('password')"
                    @keydown.enter="submitAccountDetails"
                />
                <Errors
                    v-if="errors.length"
                    class="invite__errors"
                    :class="{ 'invite__errors--password-errors': showPasswordErrorHeader }"
                    :errors="errors"
                    :error-header="errorsHeader"
                >
                    <template #error="{ error }">
                        <template v-if="error === 'Account already exists for this username.'">
                            It looks like you already have an account. Click <PocketLink :href="signInHref">
                                here
                            </PocketLink> to sign in
                            and complete the license activation process.
                        </template>
                        <template v-else>
                            {{ error }}
                        </template>
                    </template>
                </Errors>
                <div class="invite__password-toggle-container">
                    <template v-if="!createPassword">
                        <RouterLink
                            v-slot="{ href, navigate }"
                            custom
                            :to="{
                                name: 'invite',
                                query: {
                                    ...route.query,
                                    type: 'password',
                                }
                            }"
                        >
                            <PocketLink
                                class="invite__create-password-link"
                                type="tertiary-small"
                                :disabled="isLoading"
                                :href="href"
                                @click="navigate"
                            >
                                Create a password
                            </PocketLink>
                        </RouterLink> instead of using a link to sign in
                    </template>
                    <template v-else>
                        <RouterLink
                            v-slot="{ href, navigate }"
                            custom
                            :to="{
                                name: 'invite',
                                query: {
                                    ...route.query,
                                    type: undefined,
                                }
                            }"
                        >
                            <PocketLink
                                class="invite__use-magic-email-link"
                                type="tertiary-small"
                                :disabled="isLoading"
                                :href="href"
                                @click="navigate"
                            >
                                Sign in with a magic link
                            </PocketLink>
                        </RouterLink> instead of creating a password
                    </template>
                </div>
                <div class="invite__legal">
                    <Checkbox
                        v-model="agreed"
                        class="invite__legal-checkbox"
                        :disabled="isLoading"
                    />
                    <div class="invite__legal-text">
                        I agree to the

                        <a
                            target="_blank"
                            :href="`https://www.pocketprep.com/privacy-policy?${buildUTMParamString({
                                utm_campaign: 'app_settings',
                                utm_content: 'invite_privacy_policy'
                            })}`"
                            class="invite__legal-text-link"
                        >privacy policy</a>
                        and
                        <a
                            target="_blank"
                            :href="`https://www.pocketprep.com/terms-of-service?${buildUTMParamString({
                                utm_campaign: 'app_settings',
                                utm_content: 'invite_terms'
                            })}`"
                            class="invite__legal-text-link"
                        >terms of service</a>
                    </div>
                </div>
                <PocketButton
                    class="invite__submit"
                    :disabled="!firstName.trim() || !lastName.trim() || !email || !agreed
                        || (createPassword && !password)"
                    :is-loading="isLoading"
                    @click="submitAccountDetails"
                    @keydown.enter="submitAccountDetails"
                >
                    Start Studying <Icon class="invite__submit-arrow" type="arrow" />
                </PocketButton>
            </div>
        </ModalContainer>
    </Portal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as Sentry from '@sentry/browser'
import UIKit from '@pocketprep/ui-kit'
import { isSupportWindow, isValidEmail, isValidPassword, passwordPolicyErrors } from '@/utils'
import LeftBlob from '@/assets/onboarding/LeftBlob.vue'
import RightBlob from '@/assets/onboarding/RightBlob.vue'
import type { Study } from '@pocketprep/types'
import { examMetadataModule } from '@/store/examMetadata/module'
import { licenseModule } from '@/store/license/module'
import { userModule } from '@/store/user/module'
import { toastModule } from '@/store/toast/module'
import { buildUTMParamString } from '@/store/utils'

const {
    Input: PocketInput,
    Button: PocketButton,
    Link: PocketLink,
    Select: PocketSelect,
    Icon,
    Checkbox,
    Errors,
    ModalContainer,
} = UIKit

const route = useRoute()
const router = useRouter()

const errors = ref<string[]>([])
const errorFields = ref<string[]>([])
const errorsHeader = ref('')
const showPasswordErrorHeader = ref(false)
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const agreed = ref(false)
const password = ref('')
const createPassword = ref(false)
const isLoading = ref(false)
const license = ref<Study.Class.LicenseJSON | null>(null)
const licenseCode = ref<string | null>(null)
const groupCode = ref<string | null>(null)
const orgName = ref('Your school/organization')
const studentLmsId = ref<string | undefined>(undefined)

// For exam dropdown
const exam = ref<null | { value: string; label: string }>(null)

const mappedExams = computed(() => {
    const exams: string[] = []
    if (license.value) {
        exams.push(license.value.examName)
        if (license.value.linkedLicenses) {
            license.value.linkedLicenses.forEach(linkedLicense => {
                const ll = linkedLicense as Study.Class.LicenseJSON // Type to extract the examName
                exams.push(ll.examName)
            })
        }
    }
    return exams.map(e => ({
        label: e,
        value: e,
    }))
})

const licenseFromExam = computed(() => {
    let selectedLicense = license.value
    if (license.value?.linkedLicenses && exam.value?.value) {
        // Has linkedLicenses AND a selected exam, so we have to find the license in the structure
        const selectedExam = license.value.linkedLicenses.find(l => {
            const linkedLicense = l as Study.Class.LicenseJSON
            return linkedLicense.examName === exam.value?.value
        })
        if (selectedExam) {
            // Only assign if we find it
            selectedLicense = selectedExam as Study.Class.LicenseJSON
        }
    }
    return selectedLicense
})

const emailQueryParam = computed(() => {
    return typeof route.query?.email === 'string' ? route.query.email : ''
})

const licenseQueryParam = computed(() => {
    return typeof route.query?.license === 'string' ? route.query.license : ''
})

const groupCodeQueryParam = computed(() => {
    return typeof route.query?.groupCode === 'string' ? route.query.groupCode : ''
})

const studentLmsIdQueryParam = computed(() => {
    return typeof route.query?.studentLmsId === 'string' ? route.query.studentLmsId : ''
})

const signInHref = computed(() => {
    return router.resolve({
        name: 'sign-in',
        query: {
            ...(
                licenseCode.value
                && !groupCodeQueryParam.value
                && { license: licenseCode.value }
            ),                                                      // Conditionally adds license property
            ...(email.value && { email: email.value }),               // Conditionally adds email property
            ...(firstName.value && { firstName: firstName.value }),   // Conditionally adds firstName property
            ...(lastName.value && { lastName: lastName.value }),      // Conditionally adds lastName property
            ...route.query,
        },
    }).href
})

onMounted(async () => {
    isLoading.value = true
    await userModule.actions.signOut()
    await examMetadataModule.actions.fetchExamMetadata()

    email.value = emailQueryParam.value
    licenseCode.value = licenseQueryParam.value
    groupCode.value = groupCodeQueryParam.value
    studentLmsId.value = studentLmsIdQueryParam.value

    if (groupCode.value) {
        await fetchLicenseCodeForGroupCode()
    }

    if (licenseCode.value) {
        await validateLicenseCode()
        const hasAlreadyRedeemedError = errors.value.find(err => err.includes('already been redeemed'))
        if (hasAlreadyRedeemedError) {
            await router.push({
                name: 'sign-in',
                query: {
                    ...route.query,
                    error: hasAlreadyRedeemedError,
                },
            })
            return
        }
    }

    createPassword.value = route.query?.type === 'password'
    isLoading.value = false
})

const routeChanged = () => {
    errors.value = []
    errorFields.value = []
    password.value = ''
    errorsHeader.value = ''
    showPasswordErrorHeader.value = false
    createPassword.value = route.query?.type === 'password'
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}

const validateLicenseCode = async () => {
    errors.value = []
    errorFields.value = []
    errorsHeader.value = ''
    showPasswordErrorHeader.value = false
    const licenseFromExamVal = licenseFromExam.value
    const licenseCodeVal = licenseFromExamVal?.licenseKey || licenseCode.value
    try {
        if (licenseCodeVal) {
            const [ validLicense, fetchedOrgName ] = await Promise.all([
                licenseModule.actions.validateLicense(licenseCodeVal),
                licenseModule.actions.fetchOrgNameFromLicenseCode(licenseCodeVal),
            ])
            orgName.value = fetchedOrgName || orgName.value
            license.value = validLicense
        } else {
            errorFields.value.push('license')
            errors.value.push('Please enter a license code')
        }
    } catch (err) {
        errors.value.push((err as Error).toString())
        errorFields.value.push('license')
    }
}

const fetchLicenseCodeForGroupCode = async () => {
    errors.value = []
    try {
        if (groupCode.value) {
            const fetchedLicenseCode = await licenseModule.actions.fetchLicenseCodeForGroupCode(
                groupCode.value,
                studentLmsId.value
            )
            if (fetchedLicenseCode) {
                licenseCode.value = fetchedLicenseCode
            }
        }
    } catch (err) {
        errors.value.push(String(err))
    }
}

const submitAccountDetails = async () => {
    errors.value = []
    errorFields.value = []
    errorsHeader.value = ''
    showPasswordErrorHeader.value = false
    isLoading.value = true

    try {
        // validate license
        await validateLicenseCode()

        if (!license.value?.examGuid && !errorFields.value.includes('license')) {
            // A license with no examGuid probably came from legacy bulk purchase, which should be inactive
            // If this happens, we should stop the process here and flag it for review
            const legacyLicenseError = new Error(`Invalid License: ${licenseCode.value}`)
            Sentry.captureException(`Legacy License error: ${licenseCode.value}`)
            throw legacyLicenseError
        }

        if (!firstName.value) {
            errorFields.value.push('firstName')
            errors.value.push('Please enter a first name.')
        }

        if (!lastName.value) {
            errorFields.value.push('lastName')
            errors.value.push('Please enter a last name.')
        }

        if (!isValidEmail(email.value)) {
            errorFields.value.push('email')
            errors.value.push('Invalid email address.')
        }

        if (createPassword.value && !isValidPassword(password.value)) {
            errorFields.value.push('password')
            showPasswordErrorHeader.value = errorFields.value.length === 1 && errorFields.value.includes('password')
            if (showPasswordErrorHeader.value) {
                errorsHeader.value = 'Password doesn`t meet the following requirements:'
            }

            const passwordErrors = passwordPolicyErrors(showPasswordErrorHeader.value, password.value)

            errors.value = [ ...errors.value, ...passwordErrors ]
        }

        if (!agreed.value) {
            errors.value.push('You must agree to the terms of service and privacy policy to continue.')
        }

        if (!errors.value.length && license.value?.examGuid) {
            // Licenses don't specify exam versions, so we get the most recent
            const examVal = examMetadataModule.getters.getMostRecentExamForExamGuid(license.value.examGuid)
            if (examVal) {
                await userModule.actions.createUser({
                    email: email.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    password: password.value,
                    exam: examVal,
                })
            }

            await licenseModule.actions.activateLicense(license.value, {
                studentLmsId: studentLmsIdQueryParam.value,
            })
            await router.push({ name: 'study' })
            toastModule.actions.displayToast({
                title: 'License code activated.',
                subtext: `You are now studying for the ${license.value.examName} exam. Switch exams via the upper
                    left menu dropdown.`,
            })
        }
    } catch (err) {
        const error = (err as { error?: string }).error || (err as { message?: string }).message
        if (!err) {
            errors.value.push('Unable to create account.')
        } else if (error) {
            errors.value.push(error)
        } else if (err instanceof Error || typeof err === 'string') {
            errors.value.push(err.toString())
        }
    } finally {
        isLoading.value = false
    }
}

watch(() => route.fullPath, routeChanged)
</script>
<style lang="scss" scoped>
.invite {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    background-color: rgba($gray-background, 0.4);
    width: 964px;
    min-height: 584px;

    @include breakpoint (brown-bear) {
        width: 740px;
    }

    @include breakpoint (black-bear) {
        width: 100%;
        min-height: 100%;
    }

    &__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    &__head-right {
        display: flex;
        align-items: center;
        justify-content: space-between;

        span {
            color: $pewter;
            margin: 0 8px;
            display: block;
        }
    }

    &__logo {
        height: 27px;
    }

    &__left-blob {
        position: absolute;
        bottom: 0;
        left: 0;
        border-radius: 0 0 0 12px;

        @include breakpoint (black-bear) {
            display: none;
        }
    }

    &__right-blob {
        position: absolute;
        top: 0;
        right: 0;

        @include breakpoint (black-bear) {
            display: none;
        }
    }

    &__title {
        font-weight: 700;
        font-size: 26px;
        line-height: 31px;
        margin-top: 46px;
        margin-bottom: 9px;
    }

    &__info {
        width: 320px;
        color: $slate-03;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-bottom: 34px;
    }

    &__premium-access {
        font-weight: 600;
    }

    &__exam-name {
        font-weight: 600;
    }

    &__select-exam,
    &__first-name,
    &__last-name {
        width: 320px;
        margin-bottom: 19px;
    }

    &__email-container {
        position: relative;
        color: $slate-03;
    }

    &__email {
        width: 320px;
        margin-bottom: 16px;
    }

    &__email-tip {
        position: absolute;
        width: 208px;
        right: -258px;
        top: 5px;
        line-height: 18px;
        font-size: 13px;

        @include breakpoint (brown-bear) {
            width: 171px;
            right: -189px;
            top: 10px;
        }

        @include breakpoint (black-bear) {
            position: relative;
            width: 294px;
            top: auto;
            right: auto;
            margin-bottom: 30px;
            margin-left: 3px;
        }

        &::before {
            content: '';
            position: absolute;
            left: -68px;
            top: -9px;
            background: url('@/assets/onboarding/tip-arrow.svg') no-repeat center center;
            width: 65px;
            height: 14px;
            transform: rotate(-10deg);

            @include breakpoint (black-bear) {
                display: none;
            }
        }
    }

    &__email-tip-title {
        margin-bottom: 6px;
        font-weight: 600;
        display: block;

        @include breakpoint (brown-bear) {
            margin-bottom: 8px;
        }
    }

    &__email-tip-description {
        @include breakpoint(black-bear) {
            width: 262px;
        }
    }

    &__email-tip-link {
        font-size: 13px !important;
    }

    &__password {
        width: 320px;
        margin-bottom: 16px;
    }

    &__license {
        width: 320px;
        margin-bottom: 16px;
    }

    &__password-toggle-container {
        font-size: 13px;
        line-height: 18px;
        margin-bottom: 32px;
        margin-left: 11px;

        @include breakpoint (black-bear) {
            margin-bottom: 24px;
        }
    }

    &__create-password-link,
    &__use-magic-email-link {
        font-size: 13px !important;
        font-weight: 500 !important;
    }

    &__legal {
        font-size: 13px;
        line-height: 18px;
        margin-left: 4px;
        display: flex;
        align-items: center;
    }

    &__legal-checkbox {
        margin-right: 9px;
        vertical-align: middle;
        margin-bottom: 2px;
    }

    &__legal-text-link {
        color: $brand-blue;
        text-decoration: none;
    }

    &__errors {
        box-sizing: border-box;
        width: 320px;
        margin-bottom: 16px;

        &--password-errors {
            :deep(.uikit-errors__error) {
                &::before {
                    content: '•';
                    display: inline-block;
                    margin-right: 5px;
                }
            }
        }
    }

    &__submit {
        margin: 42px auto 40px;
        display: block;

        @include breakpoint (black-bear) {
            margin-bottom: 64px;
        }
    }

    &__submit-arrow {
        margin-left: 10px;
    }
}
</style>
